<template>
  <v-navigation-drawer
    :value="drawer"
    app
    clipped
    dark
    color="primary"
    class="axp-nav"
    :temporary="false"
    v-on:input="drawerStateChange"
  >
    <div class="nav-close-wrapper ml-2">
      <v-btn icon class="nav-close-btn" @click="$emit('closeDrawer')" aria-label="Close navigation">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-list>
      <v-container
        v-for="(item, itemIndex) in filteredGlobalOptions"
        :key="`global-${item.to || item.title}-${itemIndex}`"
      >
        <v-list-item
          v-if="HasGlobalSubOptions(item)"
          :to="item.to"
          class="tile"
          v-on:click="OnExpandableGlobalOptionPress(item.title)"
          exact
          dense
        >
          <v-list-item-icon>
            <v-icon size="20">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
          <v-list-item-icon>
            <v-icon size="20">{{ IsGlobalOptionExpanded(item.title) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-list-item-icon>
        </v-list-item>

        <v-list-item
          v-else
          :to="item.to"
          class="tile"
          v-on:click="OnGlobalOptionPress"
          exact
          dense
        >
          <v-list-item-icon>
            <v-icon size="20">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>

        <v-list-item
          v-for="(subItem, subIndex) in IsGlobalOptionExpanded(item.title) ? item.subOptions || [] : []"
          :key="`global-sub-${item.to || item.title}-${subItem.to || subItem.title}-${subIndex}`"
          class="subItem"
          :to="subItem.to"
          v-on:click="OnGlobalOptionPress"
          exact
          dense
        >
          <div class="subItem__indent" aria-hidden="true"></div>
          <v-list-item-icon>
            <v-icon size="18">{{ subItem.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ subItem.title }}</v-list-item-title>
        </v-list-item>
      </v-container>

      <v-divider></v-divider>

      <v-container
        v-for="(sect, sectIndex) in sectionHeaders"
        :key="`section-${sect.id || sect.name}-${sectIndex}`"
      >
        <v-list-item
          v-on:click="OnSectionPress(sect.name)"
          dense
          :class="[
            'section-header',
            section !== null && sect.id === section.id ? 'primaryLight' : '',
          ]"
        >
          <v-list-item-title class="section-header__title">
            {{ sect.name }}
          </v-list-item-title>
        </v-list-item>

        <v-container v-if="section !== null && sect.id === section.id">
          <v-list-item
            v-for="(item, sectionItemIndex) in filteredSectionOptions"
            :key="`section-item-${sect.id || sect.name}-${item.to || item.title}-${sectionItemIndex}`"
            class="subItem"
            :to="item.to"
            exact
            dense
          >
            <div class="subItem__indent" aria-hidden="true"></div>
            <v-list-item-icon>
              <v-icon size="20">{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-container>
      </v-container>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { mapActions } from "vuex";
import { mapState } from "vuex";
import { mapMutations } from "vuex";
import { mapGetters } from "vuex";

export default {
  props: {
    drawer: {
      default: null,
      type: Boolean,
    },
  },
  computed: {
    isDrawerOpen() {
      return this.drawer;
    },
    filteredGlobalOptions() {
      return this.globalOptions.filter(
        (item) =>
          !(
            item.title == "Add / Update Assets" &&
            !this.CanUserAccessAddUpdateAssets()
          ) &&
          !(
            item.title == "Search Index" &&
            !this.CanUserAccessProductSearchReindex()
          )
      );
    },
    filteredSectionOptions() {
      return this.sectionOptions.filter(
        (item) =>
          !(
            item.title == "Add / Update Assets" &&
            !this.CanUserAccessAddUpdateAssets()
          )
      );
    },
    ...mapState("navstate", ["globalOptions", "sectionOptions"]),
    ...mapState("assetxpstate", ["sectionHeaders", "section"]),
  },
  mounted: function() {
    this.GetSectionHeaders();
    this.InitializeGlobalOptionExpansion();
  },
  data: () => ({
    expandedGlobalOptions: {},
  }),
  methods: {
    drawerStateChange(isOpen) {
      this.SetDrawerState(isOpen);
    },
    ...mapGetters("authentication", [
      "CanUserAccessAddUpdateAssets",
      "CanUserAccessProductSearchReindex",
    ]),
    ...mapActions("assetxpstate", [
      "GetSectionHeaders",
      "ResetSelectedSection",
      "ResetSelectedAsset",
    ]),
    ...mapActions("navstate", ["GetSelectedSection"]),
    ...mapMutations("navstate", ["SetDrawerState"]),
    OnSectionPress(sectionName) {
      this.GetSelectedSection(sectionName);
      this.ResetSelectedAsset();
      this.$router.push({
        name: "Section Search",
        params: { sectionName: sectionName },
      });
    },
    OnGlobalOptionPress() {
      this.ResetSelectedSection();
      this.ResetSelectedAsset();
    },
    HasGlobalSubOptions(item) {
      return item.subOptions != null && item.subOptions.length > 0;
    },
    IsGlobalOptionExpanded(title) {
      if (this.ShouldAutoExpandGlobalOption(title)) {
        return true;
      }
      return this.expandedGlobalOptions[title] === true;
    },
    ShouldAutoExpandGlobalOption(title) {
      if (title !== "PDF > AutoPOG") {
        return false;
      }

      const path = this.$route.path || "";
      return (
        path === "/pdf2excel" ||
        path.startsWith("/pdf2excel/") ||
        path === "/pdf2pog" ||
        path.startsWith("/pdf2pog/")
      );
    },
    OnExpandableGlobalOptionPress(title) {
      this.OnGlobalOptionPress();
      this.$set(this.expandedGlobalOptions, title, !this.IsGlobalOptionExpanded(title));
    },
    InitializeGlobalOptionExpansion() {
      for (let i = 0; i < this.globalOptions.length; i++) {
        const option = this.globalOptions[i];
        if (
          this.HasGlobalSubOptions(option) &&
          this.expandedGlobalOptions[option.title] === undefined
        ) {
          this.$set(this.expandedGlobalOptions, option.title, false);
        }
      }
    },
  },
  watch: {
    globalOptions: {
      handler() {
        this.InitializeGlobalOptionExpansion();
      },
      immediate: true,
    },
    section: {
      handler(newVal) {
        console.log("Section changed", newVal);
      },
    },
  },
};
</script>

<style></style>
